/* This is kept separate from main.tsx so that hot module reloading won't cause
 * warnings about re-rendering and re-creating a react root on an HTML element
 * that already has an attached root element.
 */
import "./eres.css"
import * as P from "./program-state"
import * as Rrd from "react-router-dom"
import * as G from "./global-state"
import Audience_page from "./pages/audiences"
import Brand from "./pages/brands"
import {Page_edit_brand} from "./pages/brands-individual-editor"
import Connected_network_editor from "./pages/connected-networks-individual-editor"
import Connected_networks from "./pages/connected-networks"
import Create_brand from "./pages/brands-create"
import Dashboard from "./pages/dashboard"
import Campaign_list from "./pages/campaigns"
import Create_campaign from "./pages/campaigns-create"
import Campaign_editor from "./pages/campaigns-individual-editor"
import React from "react"
import {Login_page, Logout_page, Sign_up_page, Forgot_password_page, Reset_password_page} from "./pages/login-logout"
import Create_audience from "./pages/audiences-create"
import Audience_detail from "./pages/audiences-individual-editor"
import Account_infomation from "./pages/account"
import Generator from "./pages/generator"
import Wizard_legacy from "./pages/wizard-legacy"
import Onboarding from "./pages/wizard"
import Custom_audience_page from "./pages/custom-audiences"
import Create_custom_audience from "./pages/custom-audience-create"
import Custom_audience_individual_view from "./pages/custom-audience-individual-view"
import Plans from "./pages/plans"
import Integration_error from "./pages/integration-error"


type error_boundary_state = { has_error : boolean | "has_unauthenticated" }

const do_redirect_to_login = async (navigate : Rrd.NavigateFunction, redirect_to : string) => {
    if (!redirect_to.startsWith("/login")) {
        navigate(`/login?redirect-to=${redirect_to}`)
    }
}

const Redirect_to_login = ({children} : React.PropsWithChildren<{}>) => {
    const loc = Rrd.useLocation()
    const nav = Rrd.useNavigate()
    React.useEffect(() => {
        const redirect_to = loc.pathname
        void do_redirect_to_login(nav, redirect_to)
    })
    return <>{children}</>
}

class Unauthenticated_error_boundary extends React.Component<React.PropsWithChildren<{}>, error_boundary_state> {
    constructor(props : {}) {
        super(props)
        this.state = { has_error : false }
    }

    static getDerivedStateFromError(error : any) : error_boundary_state {
        const ret : error_boundary_state = { has_error : error instanceof P.Unauthenticated_error ? "has_unauthenticated" : true }
        if (ret.has_error === "has_unauthenticated") {
            delete error.stack
        }
        return ret
    }

    render() {
        switch (this.state.has_error) {
        case false: return this.props.children
        case true: return <p></p>
        case "has_unauthenticated": return <Redirect_to_login />
        }
    }
}

const Error_handler = () => {
    let err = Rrd.useRouteError()
    const loc = Rrd.useLocation()
    const nav = Rrd.useNavigate()
    React.useEffect(() => {
        if (err instanceof P.Unauthenticated_error) {
            const redirect_to = loc.pathname
            void do_redirect_to_login(nav, redirect_to)
        } else {
            //throw err
        }
    })
    console.log("Unexpected error:", err)
    if (Rrd.isRouteErrorResponse(err)) {
    return (<div>
        <h2><b>Unexpected error:</b></h2>
        <p><b>Status:</b> {err.status}</p>
        <p><b>Status text:</b> {err.statusText}</p>
        <p><b>Data:</b> <pre>{err.data}</pre></p>
    </div>)
    }
    if (err instanceof Error) {
        return (<div>
        <h2><b>Unexpected error:</b></h2>
        <p><b>Name:</b> {err.name}</p>
        <p><b>Message:</b> {err.message}</p>
        <p><b>Stack:</b><pre>{err.stack}</pre></p>
        </div>)
    }
    return <div>Unexpected error</div>
}

const Redirect_to_dashboard = () => {
    const nav = Rrd.useNavigate()
    P.run_once_on_component_creation(() => { nav("/dashboard") })
    return null
}

const router = Rrd.createBrowserRouter([
    {path: "/", errorElement: <Error_handler/>, element: <Rrd.Outlet/>, children: [
        {index: true, element: <Redirect_to_dashboard/>},
        {path: "/dashboard", element: <Dashboard/>},
        {path: "/sign-up", element: <Sign_up_page/>},
        {path: "/forgot-password", element: <Forgot_password_page/>},
        {path: "/reset-password", element: <Reset_password_page/>},
        {path: "/login", element: <Login_page/>},
        {path: "/logout", element: <Logout_page/>},
        {path: "/audiences", element: <Audience_page/>},
        {path: "/audiences/create", element: <Create_audience/>},
        {path: "/audiences/:audience_id", element: <Audience_detail/>},
        {path: "/custom-audiences", element: <Custom_audience_page/>},
        {path: "/custom-audiences/create", element: <Create_custom_audience />},
        {path: "/custom-audiences/:custom_audience_id", element: <Custom_audience_individual_view/>},
        {path: "/brands", element: <Brand/>},
        {path: "/brands/create", element: <Create_brand/>},
        {path: "/brands/:brand_id", element: <Page_edit_brand/>},
        {path: "/connected-networks", element: <Connected_networks/>},
        {path: "/connected-networks/:prefixed_dbid", element: <Connected_network_editor/>},

        {path: "/campaigns", element: <Campaign_list/>},
        {path: "/campaigns/create", element: <Create_campaign/>},
        {path: "/campaigns/edit/:prefixed_dbid", element: <Campaign_editor/>},

        {path: "/connected-networks/:prefixed_dbid", element: <Connected_network_editor/>},
        {path: "/connected-networks/:prefixed_dbid", element: <Connected_network_editor/>},

        {path: "/account", element: <Account_infomation/>},
        {path: "/account/billing/result", element: <Account_infomation/>},
        {path: "/plans", element: <Plans/>},
        {path: "/generator", element: <Generator />},
        {path: "/wizard-legacy", element: <Wizard_legacy />},
        {path: "/wizard", element: <Onboarding />},
        {path: "/onboarding", element: <Onboarding />},
        {path: "/integration/error", element: <Integration_error />},
    ]}
])

export const React_root = () => {
    return (
    <React.StrictMode>
    <P.Program_state_react_context.Provider value={G._ps}>
    <Unauthenticated_error_boundary>
    <Rrd.RouterProvider router={router}/>
    </Unauthenticated_error_boundary>
    </P.Program_state_react_context.Provider>
    </React.StrictMode>)
}
